import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ArrowLeft } from 'lucide-react';
import { useOverlayHistory } from '@/components/ui/overlayHistory';

/**
 * FullScreenPage — universal full-screen overlay that replaces popups/dialogs.
 *
 * Features:
 *  - Fixed inset-0 overlay, no gap at top (safe-area-aware)
 *  - Body scroll lock with cleanup
 *  - Left-edge back-swipe gesture (touch devices)
 *  - Always: ← back arrow + title header. No X button.
 *  - Scrollable content area via flex-1 overflow-y-auto
 *
 * Props:
 *  open        — boolean
 *  onClose     — () => void
 *  title       — string (shown in header)
 *  subtitle    — string (optional, shown below title)
 *  rightAction — ReactNode (optional button/icon in header right)
 *  bg          — tailwind bg class (default 'bg-background')
 *  noPadding   — skip default px-4 padding on content area
 *  children
 */
export default function FullScreenPage({
  open,
  onClose,
  title,
  subtitle,
  rightAction,
  bg = 'bg-background',
  noPadding = false,
  flexContent = false,
  className = '',
  children,
}) {
  const overlayRef = useRef(null);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      // iOS WebKit can retain horizontal scroll offset after overlay closes
      window.scrollTo(0, window.scrollY);
    };
  }, [open]);

  // Browser back / swipe-back: shared depth-based stack so nested overlays
  // (Dialog → Sheet → Dialog, etc.) close one-at-a-time instead of cascading.
  useOverlayHistory(onClose, open);

  // Left-edge back-swipe — attached to overlay with capture:true so it fires
  // before scrollable children consume the touch event
  useEffect(() => {
    if (!open) return;
    const el = overlayRef.current;
    if (!el) return;
    let startX = 0;
    let startY = 0;
    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = Math.abs(e.changedTouches[0].clientY - startY);
      // Only trigger if swipe starts within 40px of left edge, mostly horizontal
      if (startX < 40 && dx > 72 && dy < 80) onClose();
    };
    el.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true, capture: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart, { capture: true });
      el.removeEventListener('touchend', onTouchEnd, { capture: true });
    };
  }, [open, onClose]);

  if (!open) return null;

  const overlay = (
    <div
      ref={overlayRef}
      className={`fixed inset-0 z-[200] flex flex-col ${bg} text-foreground ${className}`}
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* ── Header ── */}
      <div className="flex-shrink-0 flex items-center gap-2 px-2 border-b border-border"
        style={{ minHeight: 56 }}>
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-muted active:bg-muted transition-colors flex-shrink-0"
          aria-label="Go back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          {title && (
            <div
              className="text-base font-bold leading-tight truncate"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {title}
            </div>
          )}
          {subtitle && (
            <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
          )}
        </div>
        {rightAction && <div className="flex-shrink-0 pr-2">{rightAction}</div>}
      </div>

      {/* ── Content ── */}
      <div className={`flex-1 min-h-0 ${flexContent ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'} ${noPadding ? '' : 'px-4'}`}>
        {children}
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
}
